(function (global) {
    /**
     * @typedef PlaceholderGroup
     * @property {string} title
     * @property {number} position
     * @property {Placeholder[]} children
     */

    /**
     * @typedef Placeholder
     * @property {string} name
     * @property {string} title
     * @property {string=} description
     * @property {number} position
     */

    /**
     * @param {{RestrictToOrganizationUnits: boolean, Form: *}} options
     * @return {{Placeholders: PlaceholderGroup[], Map: {}}}
     */
    function getAvailablePlaceholders(options) {
        let placeholders = [];

        if (options.RestrictToOrganizationUnits) {
            placeholders = getOrganizationUnitCheckpointPlaceholders();
        } else {
            if (options.Form) {
                placeholders = placeholders.concat(getFormParameterPlaceholders(options.Form));
            }

            const maxPosition = placeholders.length > 0
                ? placeholders.reduce(function (max, group) {
                    return max > group.position ? max : group.position;
                }, -1)
                : -1;

            const issuePlaceholdersData = getIssuePlaceholders(maxPosition);
            const linkPlaceholdersData = getLinkPlaceholders.call(issuePlaceholdersData.MaxPosition);

            placeholders = placeholders.concat(issuePlaceholdersData.Placeholders, linkPlaceholdersData.Placeholders);
        }

        const placeholdersMap = {};
        placeholders
            .filter(function (group) {
                return (group.children || []).length;
            })
            .forEach(function (group) {
                group.children
                    .filter(function (fn) {
                        return !!fn.name;
                    })
                    .forEach(function (fn) {
                        placeholdersMap[fn.name.toLowerCase()] = fn;
                    }, this);
            }, this);

        return { Placeholders: placeholders, Map: placeholdersMap };
    }

    /**
     *
     * @param {*} form
     * @return {PlaceholderGroup[]}
     */
    function getFormParameterPlaceholders(form) {
        if (!form || !(form.Parametergroups || []).length) {
            return [];
        }

        return form.Parametergroups
            .filter(function (group) {
                return group.Enabled &&
                    group.ModificationType !== Enums.ModificationType.Deleted &&
                    (group.Parameters || []).length > 0;
            })
            .map(function (group) {
                return {
                    title: group.Title,
                    position: group.Position,
                    children: group.Parameters
                        .filter(function (param) {
                            return param.Enabled &&
                                param.ModificationType !== Enums.ModificationType.Deleted &&
                                param.Type !== Enums.elementType.Info;
                        })
                        .map(function (param) {
                            let description = `<span class="item-group">
                                ${i18next.t('changeMode.mailTemplates.placeholdersWindow.checkpointGroup')}: ${group.Title}
                            </span>`;

                            if (!!param.Description) {
                                description += `<br><br>${param.Description}`;
                            }

                            return {
                                name: `CP_${param.OID}`,
                                title: $.trim(param.Title),
                                description: description,
                                position: param.Position
                            };
                        })
                };
            });
    }

    /**
     * @param {number} maxPosition
     * @return {{Placeholders: PlaceholderGroup[], MaxPosition: number}}
     */
    function getIssuePlaceholders(maxPosition) {
        if (this.RestrictToOrganizationUnits) {
            return { Placeholders: [], MaxPosition: maxPosition };
        }

        const issuePlaceholders = [
            {
                title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.issuePlaceholders.title'),
                position: ++maxPosition,
                children: [
                    {
                        name: 'IssueCreatorName',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.issueCreatorName.title'),
                        description: getIssuePlaceholderDescription('issueCreatorName')
                    },
                    {
                        name: 'IssueCreationTimestamp',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.issueCreationTimestamp.title'),
                        description: getIssuePlaceholderDescription('issueCreationTimestamp')
                    },
                    {
                        name: 'IssueEditorName',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.issueEditorName.title'),
                        description: getIssuePlaceholderDescription('issueEditorName')
                    },
                    {
                        name: 'IssueModificationTimestamp',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.issueModificationTimestamp.title'),
                        description: getIssuePlaceholderDescription('issueModificationTimestamp')
                    },
                    {
                        name: 'IssueTitle',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.issueTitle.title'),
                        description: getIssuePlaceholderDescription('issueTitle')
                    },
                    {
                        name: 'IssueDescription',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.issueDescription.title'),
                        description: getIssuePlaceholderDescription('issueDescription')
                    },
                    {
                        name: 'IssueLocationTitle',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.issueLocationTitle.title'),
                        description: getIssuePlaceholderDescription('issueLocationTitle')
                    },
                    {
                        name: 'IssueType',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.issueType.title'),
                        description: getIssuePlaceholderDescription('issueType')
                    },
                    {
                        name: 'IssueFormTitle',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.issueFormTitle.title'),
                        description: getIssuePlaceholderDescription('issueFormTitle')
                    },
                    {
                        name: 'IssueDeadlineTimestamp',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.issueDeadlineTimestamp.title'),
                        description: getIssuePlaceholderDescription('issueDeadlineTimestamp')
                    },
                    {
                        name: 'IssueStateTitle',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.issueStateTitle.title'),
                        description: getIssuePlaceholderDescription('issueStateTitle')
                    },
                    {
                        name: 'IssuePriorityTitle',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.issuePriorityTitle.title'),
                        description: getIssuePlaceholderDescription('issuePriorityTitle')
                    },
                    {
                        name: 'IssueId',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.issueId.title'),
                        description: getIssuePlaceholderDescription('issueId')
                    },
                    {
                        name: 'IssueRevision',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.issueRevision.title'),
                        description: getIssuePlaceholderDescription('issueRevision')
                    },
                    {
                        name: 'OpenRecordingLink',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.openRecordingLink.title'),
                        description: getIssuePlaceholderDescription('openRecordingLink')
                    }
                ]
            }];

        return { Placeholders: issuePlaceholders, MaxPosition: maxPosition };
    }

    /**
     * @param {number} maxPosition
     * @return {{Placeholders: PlaceholderGroup[], MaxPosition: number}}
     */
    function getLinkPlaceholders(maxPosition) {
        const placeholders = [];

        if (this.Form && this.Form.IsInvestigation) {
            placeholders.push({
                title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.investigationPlaceholders.title'),
                position: ++maxPosition,
                children: [
                    {
                        name: 'OpenInvestigationLink',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.openInvestigationLink.title'),
                        description: getPlaceholderDescription('investigationPlaceholders', 'openInvestigationLink')
                    }
                ]
            });
        }

        if (this.Form && Tools.IsContactModuleAvailable()) {
            placeholders.push({
                title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.sharedCorrectiveActionsPlaceholders.title'),
                position: ++maxPosition,
                children: [
                    {
                        name: 'OpenSharedCorrectiveActionsLink',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.openSharedCorrectiveActionsLink.title'),
                        description: getPlaceholderDescription('sharedCorrectiveActionsPlaceholders', 'openSharedCorrectiveActionsLink')
                    }
                ]
            });
        }

        return { Placeholders: placeholders, MaxPosition: maxPosition };
    }

    /**
     * @return {PlaceholderGroup[]}
     */
    function getOrganizationUnitCheckpointPlaceholders() {
        return [
            {
                title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.recorditemPlaceholders.title'),
                position: 1,
                children: [
                    {
                        name: 'RecorditemCreatorName',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.recorditemCreatorName.title')
                    },
                    {
                        name: 'RecorditemCreationTimestamp',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.recorditemCreationTimestamp.title')
                    },
                    {
                        name: 'RecorditemEditorName',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.recorditemEditorName.title')
                    },
                    {
                        name: 'RecorditemModificationTimestamp',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.recorditemModificationTimestamp.title')
                    },
                    {
                        name: 'RecorditemCheckpointGroupTitle',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.recorditemCheckpointGroupTitle.title')
                    },
                    {
                        name: 'RecorditemCheckpointTitle',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.recorditemCheckpointTitle.title')
                    },
                    {
                        name: 'RecorditemLocationTitle',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.recorditemLocationTitle.title')
                    },
                    {
                        name: 'RecorditemCategory',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.recorditemCategory.title')
                    },
                    {
                        name: 'RecorditemValue',
                        title: i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.recorditemValue.title')
                    }
                ]
            }
        ];
    }

    /**
     * @param {string} key
     * @return {string}
     */
    function getIssuePlaceholderDescription(key) {
        return getPlaceholderDescription('issuePlaceholders', key);
    }

    /**
     * @param {string} groupKey
     * @param {string} placeholderKey
     * @return {string|null}
     */
    function getPlaceholderDescription(groupKey, placeholderKey) {
        if (!groupKey || !placeholderKey) {
            return null;
        }

        groupKey[0] = groupKey[0].toLowerCase();
        placeholderKey[0] = placeholderKey[0].toLowerCase();

        let description = '<span class="item-group">' +
            i18next.t('changeMode.mailTemplates.placeholdersWindow.placeholders.' + groupKey + '.title') +
            '</span>';

        description += '<br><br>' +
            i18next.t(
                'changeMode.mailTemplates.placeholdersWindow.placeholders.' +
                placeholderKey +
                '.description'
            );

        return description;
    }

    global.MailTemplates = {
        getAvailablePlaceholders: getAvailablePlaceholders
    };
})(Modifications.Tools || (Modifications.Tools = {}));